import { RumPortal } from '../rum/rum.models';

export type GuidePlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

export interface GuideStep {
    /** CSS selector, preferably `[data-guide="..."]`; omitted for a centred step. */
    target?: string;
    title: string;
    body: string;
    placement?: GuidePlacement;
    /** Skipped when the target is not on the page (feature off for this tenant or role). */
    optional?: boolean;
}

export interface GuideDefinition {
    id: string;
    /** Bump to show a changed guide again to people who saw the old one. */
    version: number;
    portal: RumPortal;
    title: string;
    description: string;
    steps: GuideStep[];
    /** Starts once per browser when the router path matches. */
    autoStartPath?: RegExp;
    /** Where a replay from Help navigates first when the current page does not match. */
    startUrl?: string;
}

export type SurveyScale = 'csat5' | 'nps10';

export type ScoreBand = 'low' | 'mid' | 'high';

export type SurveyTrigger =
    | { type: 'event'; name: string }
    | { type: 'pageViews'; count: number };

export interface SurveyDefinition {
    id: string;
    version: number;
    portal: RumPortal;
    scale: SurveyScale;
    question: string;
    /** Follow-up question shown once a score is chosen, by how good the score is. */
    followUps: Record<ScoreBand, string>;
    /** Labels under the lowest and highest score. */
    lowLabel: string;
    highLabel: string;
    trigger: SurveyTrigger;
    delayMs: number;
    /** After a dismissal, stay away this long. */
    cooldownDays: number;
    /** Dismissals tolerated before the survey stops asking. */
    maxDismissals: number;
}

export const SCALE_RANGE: Record<SurveyScale, { min: number; max: number }> = {
    csat5: { min: 1, max: 5 },
    nps10: { min: 0, max: 10 }
};

export const MAX_SURVEY_COMMENT_LENGTH = 500;

/** NPS: detractors 0-6, passives 7-8, promoters 9-10. CSAT: 1-2, 3, 4-5. */
export function scoreBand(scale: SurveyScale, score: number): ScoreBand {
    const [lowMax, midMax] = scale === 'nps10' ? [6, 8] : [2, 3];

    if (score <= lowMax) return 'low';

    return score <= midMax ? 'mid' : 'high';
}
